import React from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import { useT } from '../../i18n/LanguageContext';
import { planData } from '../../i18n/translations';

/**
 * Folie 17 — Solution Pläne (SP1 – SP7).
 * Layout: fixed left column with row labels + 7 aligned plan cards on the right.
 * SP4 is the highlighted / recommended plan (gold pulsing border).
 * SP1 = entry-level plan starting at $250 (updated Feb 2026 — was $100).
 */
export default function SectionPlaene() {
  const { t, lang } = useT();
  const oneYear = t.plaene.oneYear;
  const L = t.plaene.rowLabels;
  const FROM = { de: 'ab', en: 'from', hu: 'min.', tr: 'min.' };
  const from = FROM[lang] || 'ab';

  // Row spec — order matches the original PPTX row order.
  const rows = [
    { key: 'delivery',       label: L.delivery,       value: () => oneYear },
    { key: 'price',          label: L.price,          value: (p) => `${from} ${p.from}` },
    { key: 'carat',          label: L.carat,          value: (p) => (p.caratFrom ? `${from} ${p.carat}` : p.carat) },
    { key: 'monthly',        label: L.monthly,        value: (p) => p.monthly },
    { key: 'totalDiscount',  label: L.totalDiscount,  value: (p) => p.total, emphasise: true },
  ];

  const cols = planData; // 7 plans (SP1 – SP7)

  return (
    <SectionShell id="plaene" className="">
      <Reveal>
        <div className="grid grid-cols-12 gap-6 items-end mb-8">
          <div className="col-span-12 md:col-span-6">
            <div className="overline mb-4">{t.plaene.overline}</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white max-w-[22ch]">
              {t.plaene.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6">
            <p className="font-ui text-lg md:text-xl lg:text-2xl text-white/70 md:text-right leading-snug">
              {t.plaene.sub.split('\n').map((line, i, arr) => (
                <React.Fragment key={i}>
                  {line}
                  {i < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>
        </div>
      </Reveal>

      <Reveal>
        {/* Scroll container for mobile — table stays legible on narrow viewports */}
        <div className="overflow-x-auto -mx-4 sm:mx-0 pb-2" data-testid="plans-scroll">
          <div
            className="min-w-[880px] lg:min-w-0 px-4 sm:px-0"
            style={{ paddingTop: '18px' }}
            data-testid="plans-grid"
          >
            <div
              className="grid gap-x-3"
              style={{
                gridTemplateColumns: 'minmax(150px, 200px) repeat(7, minmax(0, 1fr))',
                gridTemplateRows: 'auto repeat(5, minmax(72px, auto))',
                rowGap: 0,
              }}
            >
              {/* ─────────────── Card backgrounds (span all rows) ─────────────── */}
              {cols.map((p, i) => (
                <div
                  key={`bg-${p.id}`}
                  aria-hidden
                  data-testid={`plan-col-${p.id}`}
                  className={`plan-col ${p.recommended ? 'plan-col--recommended' : ''}`}
                  style={{
                    gridColumn: i + 2,
                    gridRow: '1 / -1',
                  }}
                >
                  {p.recommended && (
                    <span className="plan-badge-gold" data-testid="plan-recommended-badge">
                      {t.plaene.recommended}
                    </span>
                  )}
                </div>
              ))}

              {/* ─────────────── Row highlight bands (Rabatte) ─────────────── */}
              {rows.map((row, rIdx) => {
                if (row.key !== 'monthly' && row.key !== 'totalDiscount') return null;
                return (
                  <div
                    key={`band-${row.key}`}
                    aria-hidden
                    className={`plan-row-band ${row.key === 'totalDiscount' ? 'plan-row-band--strong' : ''}`}
                    style={{ gridColumn: '1 / -1', gridRow: rIdx + 2, zIndex: 1 }}
                    data-testid={`plan-row-band-${row.key}`}
                  />
                );
              })}

              {/* ─────────────── Header row: empty + SP-IDs ─────────────── */}
              <div
                style={{ gridColumn: 1, gridRow: 1 }}
                className="flex items-end pb-3 pr-4"
              >
                <span className="overline text-white/60 font-semibold">{t.plaene.headers.plan}</span>
              </div>
              {cols.map((p, i) => (
                <div
                  key={`h-${p.id}`}
                  style={{ gridColumn: i + 2, gridRow: 1, position: 'relative', zIndex: 2 }}
                  className={`flex items-end justify-center pb-3 pt-5 font-display text-2xl font-semibold ${
                    p.recommended ? 'text-[#f2d979]' : 'text-white'
                  }`}
                >
                  {p.id}
                </div>
              ))}

              {/* ─────────────── Data rows ─────────────── */}
              {rows.map((row, rIdx) => {
                const gridRow = rIdx + 2;
                return (
                  <React.Fragment key={row.key}>
                    {/* Left label */}
                    <div
                      style={{ gridColumn: 1, gridRow }}
                      className="flex items-center pr-4 border-t border-white/[0.05]"
                    >
                      <span className="font-ui text-sm sm:text-base font-semibold text-white/80 tracking-wide">
                        {row.label}
                      </span>
                    </div>
                    {/* Values across all plans */}
                    {cols.map((p, cIdx) => (
                      <div
                        key={`c-${row.key}-${p.id}`}
                        style={{
                          gridColumn: cIdx + 2,
                          gridRow,
                          position: 'relative',
                          zIndex: 2,
                        }}
                        className={`flex items-center justify-center border-t border-white/[0.05] font-ui text-base sm:text-lg font-semibold ${
                          row.emphasise
                            ? p.recommended
                              ? 'font-display text-2xl text-[#f2d979]'
                              : 'font-display text-xl text-white'
                            : p.recommended
                            ? 'text-white'
                            : 'text-white/90'
                        }`}
                        data-testid={`plan-cell-${p.id}-${row.key}`}
                      >
                        {row.value(p)}
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={220}>
        <p className="font-ui text-base md:text-lg text-white/50 italic mt-8 max-w-none leading-relaxed whitespace-nowrap overflow-hidden text-ellipsis">
          {t.plaene.footnote}
        </p>
      </Reveal>
    </SectionShell>
  );
}
